import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dispatcher } from 'dispatcher';
import { prop } from 'ramda';

import { FeaturedSlide, FeaturedSlideMobile } from './components/FeaturedSlide';

import { WidgetButton } from 'js/app/components/main/FeaturedBanner/components/WidgetButton';
import Slider from 'js/components/common/Slider';

import { useCalculateSliderHeight } from '@interlayer/js/app/components/main/FeaturedBanner/hooks/useCalculateSliderHeight';

import { DEFAULT_FEATURED_BANNER_OPTIONS, DISABLE_ROTATION_URL_PARAM, DOTS_OPTION } from 'js/app/components/main/FeaturedBanner/constants';
import { useInDesktop, usePortraitOrientation } from 'js/app/hooks/device/hooks/useMediaQuery';
import { setIsFeaturedBanner } from 'js/components/system/App/store/buzz/actions';
import { useDeviceSelector } from 'js/store/hooks';
import { getLocationParams } from 'js/utils/url.util';

import { useFilterGroup } from './hooks/useFilterGroup';

import style from './FeaturedBanner.scss';

export const composeSlides = (slides, group) => slides.concat(group?.slides || []);

export const FeaturedBanner = ({ config }) => {
    const isAutoRotate = !Object.keys(getLocationParams()).includes(DISABLE_ROTATION_URL_PARAM);

    const dispatch = useDispatch();
    const isDesktop = useInDesktop();
    const isLandscape = !usePortraitOrientation();
    const isMobile = useDeviceSelector(prop('isMobile'));
    const filterGroup = useFilterGroup();
    const [externalSlideData, setExternalSlideData] = useState(null);
    const {
        isWidgetEnabled,
        slideGroups,
        timeToDisplay,
        interfaceOptions,
        widgetButtons,
        bannerHeight: bannerHeightProp
    } = config;

    const sliderOptions = { ...DEFAULT_FEATURED_BANNER_OPTIONS };
    sliderOptions.animation.displayTime = timeToDisplay;
    sliderOptions.controls.bullets.enabled = isDesktop && interfaceOptions === DOTS_OPTION;

    const SlideComponent = isDesktop ? FeaturedSlide : FeaturedSlideMobile;

    const slidesToRender = useMemo(() => {
        const slides = (slideGroups || [])
            .reduce((acc, current) => {
                if (filterGroup(current)) {
                    acc.push({
                        ...current,
                        slides: current.slides?.filter(filterGroup) || []
                    });
                }

                return acc;
            }, [])
            .reduce(composeSlides, []);

        if (externalSlideData && slides[0]) {
            slides[0] = {
                ...slides[0],
                ...externalSlideData
            };
        }

        return slides;
    }, [externalSlideData, filterGroup, slideGroups]);

    useEffect(() => {
        dispatch(setIsFeaturedBanner(true));
        dispatcher.listen('updateSlide', setExternalSlideData);

        return () => {
            dispatch(setIsFeaturedBanner(false));
            dispatcher.removeListener('updateSlide', setExternalSlideData);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isTermsBlockNeeded = slidesToRender?.find(slide => slide.terms?.length);

    const {
        bannerHeight,
        sliderHeight,
        isCustomHeight
    } = useCalculateSliderHeight({ customHeight: bannerHeightProp, isTermsBlockNeeded });
    sliderOptions.height = sliderHeight;

    if (isMobile && isLandscape || !slidesToRender?.length) {
        return null;
    }

    return (
        <>
            <div style={{ height: sliderHeight }}>
                <Slider
                    options={sliderOptions}
                    classNames={{
                        sliderContainer: style.sliderContainer,
                        slider: style.slider,
                        bullets: style.bullets,
                        bullet: style.bullet,
                        activeBullet: style.active
                    }}
                    autoRotate={isAutoRotate}
                    infinite
                >
                    {slidesToRender.map((slide, index) => (
                        <SlideComponent
                            key={index}
                            index={index}
                            slide={slide}
                            isButtonsEnabled={isWidgetEnabled}
                            bannerButtons={widgetButtons}
                            bannerHeight={bannerHeight}
                            isCustomHeight={isCustomHeight}
                        />
                    ))}
                </Slider>
            </div>

            {isWidgetEnabled && !isDesktop && (
                <div className={style.widgetContainer}>
                    <WidgetButton widgetButtons={widgetButtons} />
                </div>
            )}
        </>
    );
};

export default FeaturedBanner;
